import type { MatrixData } from '../components/MatrixTable'
import type { ProductionSpeciesKey } from '../data/productionSpeciesPresets'
import type { ProductionUnit } from '../types/farm'
import type { WizardFarm, WizardProductionUnit } from '../types/farmWizard'
import type { Lot } from '../types/lot'

import { createMatrixProfileStore, type MatrixProfileRecord, type ResolvedMatrixProfileSelection } from './matrixProfiles'
import { getDefaultMatrixForSpecies } from './sfrMatrix'

export type SfrProfileRecord = MatrixProfileRecord
export type ResolvedSfrProfileSelection = ResolvedMatrixProfileSelection

const store = createMatrixProfileStore(
  {
    profilesKey: 'sfrProfiles',
    defaultAssignmentsKey: 'sfrDefaultProfileAssignments',
    assignmentsKey: 'sfrProfileAssignments'
  },
  getDefaultMatrixForSpecies
)

export const listFarmSfrProfiles = (
  farm: WizardFarm | undefined,
  species?: ProductionSpeciesKey
): SfrProfileRecord[] => store.listFarmProfiles(farm, species)

export const findFarmSfrProfile = (
  farm: WizardFarm | undefined,
  profileId: string | null | undefined
): SfrProfileRecord | null => store.findFarmProfile(farm, profileId)

export const readFarmDefaultSfrProfileId = (
  farm: WizardFarm | undefined,
  species: ProductionSpeciesKey
): string | null => store.readFarmDefaultProfileId(farm, species)

export const isFarmDefaultSfrProfile = (
  farm: WizardFarm | undefined,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): boolean => store.isFarmDefaultProfile(farm, species, profileId)

export const upsertFarmSfrProfile = (
  farm: WizardFarm,
  profile: SfrProfileRecord
): WizardFarm => store.upsertFarmProfile(farm, profile)

export const removeFarmSfrProfile = (
  farm: WizardFarm,
  profileId: string
): WizardFarm => store.removeFarmProfile(farm, profileId)

export const setFarmDefaultSfrProfileId = (
  farm: WizardFarm,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): WizardFarm => store.setFarmDefaultProfileId(farm, species, profileId)

export const readAssignedSfrProfileId = (
  target: Pick<ProductionUnit, 'metadata'> | Pick<Lot, 'metadata'> | undefined,
  species: ProductionSpeciesKey
): string | null => store.readAssignedProfileId(target, species)

export const setProductionUnitSfrProfileAssignment = (
  unit: WizardProductionUnit,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): WizardProductionUnit => store.setProductionUnitProfileAssignment(unit, species, profileId)

export const setLotSfrProfileAssignment = (
  lot: Lot,
  species: ProductionSpeciesKey,
  profileId: string | null | undefined
): Lot => store.setLotProfileAssignment(lot, species, profileId)

export const resolveSfrProfileSelection = (options: {
  farm?: WizardFarm
  productionUnit?: ProductionUnit
  lot?: Lot
  species: ProductionSpeciesKey
}): ResolvedSfrProfileSelection => store.resolveProfileSelection(options)

export const resolveSfrMatrixForSelection = (
  selection: ResolvedSfrProfileSelection,
  species: ProductionSpeciesKey,
  speciesDefaultMatrix?: MatrixData | null
): MatrixData => store.resolveMatrixForSelection(selection, species, speciesDefaultMatrix)

export const resolveEffectiveSfrMatrix = (options: {
  farm?: WizardFarm
  productionUnit?: ProductionUnit
  lot?: Lot
  species: ProductionSpeciesKey
  speciesDefaultMatrix?: MatrixData | null
}): MatrixData => store.resolveEffectiveMatrix(options)
